/**
 * CapabilityGuard Component
 *
 * Protects routes that require a specific Central capability.
 * Uses useCentralConsumer for capability checking.
 *
 * Suite-standard rule: **owners and admins always pass through**.
 * Capability guards gate members and viewers only. This prevents
 * false "Access Denied" screens when a capability domain hasn't been
 * registered in Central's capabilitiesRegistry yet — a known gap for
 * new domains that the backend/Central team hasn't synced.
 *
 * - Mounted inside AuthGuard → BootGate (App.tsx); never waits on a boot it
 *   cannot see: until auth + boot are settled it renders the login redirect
 *   or BootGate itself (useGuardPrerequisites), so a direct load resolves
 * - Shows access denied only for members/viewers without the capability
 *
 * ── PRECEDENCE: a terminal auth state outranks every waiting state ────────
 *
 *   1. SESSION_EXPIRED / SESSION_REVOKED / AUTH_REQUIRED → Central login
 *   2. WORKSPACE_CLOSED            → workspace-closed UI   (BootGate)
 *   3. APP_ACCESS_DENIED           → Access Denied         (BootGate)
 *   4. CENTRAL_UNAVAILABLE         → Retry Connection      (BootGate)
 *   5. a genuine authenticated data failure → the page's own error
 *
 * This guard had the identical inversion AdminGuard hung on: it waited on
 * `isLoading || !bootCompleted` BEFORE asking whether the session was still
 * alive. A terminal verdict dispatches `centralLogout()` AND
 * `setBootUnauthenticated()`, so `bootCompleted` goes false and can never come
 * back — boot has no token to boot with. Waiting on it after the answer is
 * known is waiting for ever.
 *
 * The ordering now lives in `useGuardPrerequisites`, which answers the auth
 * question before it waits on the boot, so the guard is right on its own terms
 * regardless of where it is mounted.
 *
 * HONEST LABEL — structural, not a reported bug. Via the router this inversion
 * was never reachable: all four <CapabilityGuard> routes (App.tsx) are nested
 * children of <AuthGuard><BootGate>, and AuthGuard returns
 * <Navigate to="/login"> instead of its children the moment `isAuthenticated`
 * goes false. AdminGuard hung precisely because /admin was the one branch with
 * no AuthGuard above it. Nothing here claims to fix an observed failure.
 */

import { Outlet } from 'react-router-dom';
import { useCentralConsumer } from '../../hooks/useCentralConsumer';
import { useGuardPrerequisites } from './guardPrerequisites';

interface CapabilityGuardProps {
  domain: string;
  action: string;
  children?: React.ReactNode;
}

export function CapabilityGuard({ domain, action, children }: CapabilityGuardProps) {
  const pending = useGuardPrerequisites();
  // bootCompleted is settled by useGuardPrerequisites before anything below is evaluated.
  const { hasCapability, hasWorkspaceRole } = useCentralConsumer();

  // Auth and boot first — a terminal verdict is answered before any wait.
  if (pending) return pending;

  // Owners and admins have full access — bypass capability check.
  // This prevents false denials when a capability domain is not yet
  // registered in Central's capabilitiesRegistry.
  const isOwnerOrAdmin = hasWorkspaceRole('owner', 'admin');

  if (!isOwnerOrAdmin && !hasCapability(domain, action)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md mx-auto p-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-600 mb-6">
            You don't have permission to access this feature. Please contact your administrator or upgrade your plan.
          </p>
          <a
            href="/dashboard"
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            Go to Dashboard
          </a>
        </div>
      </div>
    );
  }

  return <>{children ?? <Outlet />}</>;
}
